import * as THREE from "three";
import { WALL_THICKNESS, isFreeStanding } from "../utils/constants.js";
import { getWorldYRotation, forEachWorldCollisionBox, orientedXZPenetration } from "../utils/geometry.js";

/**
 * CollisionSystem — owns all room-bounds and inter-module collision logic.
 *
 * Extracted from EventManager to separate concerns. EventManager delegates
 * every bounds/collision query to this class via `this.collisionSystem`.
 */
export class CollisionSystem {
  // Small floating-point tolerance for AABB checks.
  // Prevents false-positive collisions from rounding errors when two modules are
  // exactly face-to-face (their half-extents sum equals the centre distance).
  // Mirrors designer-unity's approach: exact physics, no artificial tolerance.
  static COLLISION_EPSILON = 0.005;

  constructor(app) {
    this.app = app;
    // Dev-only settings toggle; skips module-vs-module tests (room bounds still apply).
    this.collisionEnabled = true;
  }

  /**
   * Get room inner boundaries (accounting for wall thickness).
   * When the room box is enabled, its dimensions take precedence.
   * @returns {{halfWidth: number, halfDepth: number}|null}
   */
  getRoomInnerBounds() {
    const sceneManager = this.app.getSceneManager();

    const roomBox = sceneManager?.getRoomBox();
    if (roomBox?.isEnabled()) {
      return {
        halfWidth: roomBox.width / 2,
        halfDepth: roomBox.depth / 2,
      };
    }

    const roomBuilder = sceneManager?.getRoomBuilder();
    if (!roomBuilder) return null;

    const roomSize = roomBuilder.getRoomSize();
    if (!roomSize) return null;

    // Only subtract wall thickness when walls are actually visible.
    // If walls are hidden (e.g. white_room environment), use the full room size.
    const wallsVisible = roomBuilder.walls?.some(w => w.visible) ?? true;
    const wallOffset = wallsVisible ? WALL_THICKNESS / 2 : 0;

    return {
      halfWidth: roomSize.width / 2 - wallOffset,
      halfDepth: roomSize.depth / 2 - wallOffset,
    };
  }

  /**
   * Calculate rotated furniture half-extents for AABB tests.
   *
   * FIX: Was using mesh.rotation.y (local rotation) which is wrong when a
   * module has been reparented or exists in a scene-graph hierarchy. Now uses
   * getWorldQuaternion so the AABB is always computed in world space.
   *
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement} furniture
   * @param {number|null} overrideRotation - If provided, use this Y rotation instead of
   *   reading from the mesh. Used to validate collision at a snap rotation rather than
   *   the current drag rotation.
   * @returns {{halfWidth: number, halfDepth: number}|null}
   */
  getFurnitureHalfExtents(furniture, overrideRotation = null) {
    const boundingBox = furniture.boundingBox;
    if (!boundingBox) return null;

    let rotation;
    if (overrideRotation !== null) {
      rotation = overrideRotation;
    } else {
      const mesh = furniture.getMesh();
      if (!mesh) return null;
      // World yaw so reparented/rotated modules are handled correctly.
      rotation = getWorldYRotation(mesh);
    }

    const cos = Math.abs(Math.cos(rotation));
    const sin = Math.abs(Math.sin(rotation));

    return {
      halfWidth: (boundingBox.size.x * cos + boundingBox.size.z * sin) / 2,
      halfDepth: (boundingBox.size.x * sin + boundingBox.size.z * cos) / 2,
      halfHeight: boundingBox.size.y / 2,
      localCenterY: boundingBox.localCenterOffset?.y ?? 0,
    };
  }

  /**
   * Clamp a position to keep furniture within room bounds.
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement} furniture
   * @param {THREE.Vector3} position
   * @returns {THREE.Vector3}
   */
  clampPositionToRoomBounds(furniture, position) {
    const roomBounds = this.getRoomInnerBounds();
    if (!roomBounds) return position;

    const extents = this.getFurnitureHalfExtents(furniture);
    if (!extents) return position;

    const clamped = position.clone();
    clamped.x = THREE.MathUtils.clamp(
      position.x,
      -roomBounds.halfWidth + extents.halfWidth,
      roomBounds.halfWidth - extents.halfWidth
    );
    clamped.z = THREE.MathUtils.clamp(
      position.z,
      -roomBounds.halfDepth + extents.halfDepth,
      roomBounds.halfDepth - extents.halfDepth
    );
    return clamped;
  }

  /**
   * Clamp a movement delta so that every piece in a multi-select group stays
   * within room bounds, preserving relative positions.
   * @param {Array<{furniture, position: THREE.Vector3}>} furniturePositions
   * @param {THREE.Vector3} delta
   * @returns {THREE.Vector3}
   */
  clampDeltaForGroup(furniturePositions, delta) {
    const roomBounds = this.getRoomInnerBounds();
    if (!roomBounds) return delta;

    let minDeltaX = delta.x;
    let maxDeltaX = delta.x;
    let minDeltaZ = delta.z;
    let maxDeltaZ = delta.z;

    furniturePositions.forEach(({ furniture, position }) => {
      const extents = this.getFurnitureHalfExtents(furniture);
      if (!extents) return;

      const newX = position.x + delta.x;
      const newZ = position.z + delta.z;

      const minX = -roomBounds.halfWidth + extents.halfWidth;
      const maxX = roomBounds.halfWidth - extents.halfWidth;
      const minZ = -roomBounds.halfDepth + extents.halfDepth;
      const maxZ = roomBounds.halfDepth - extents.halfDepth;

      if (newX < minX) {
        const allowedDelta = minX - position.x;
        if (allowedDelta > minDeltaX) minDeltaX = allowedDelta;
        if (delta.x < 0) maxDeltaX = Math.max(maxDeltaX, allowedDelta);
      }
      if (newX > maxX) {
        const allowedDelta = maxX - position.x;
        if (allowedDelta < maxDeltaX) maxDeltaX = allowedDelta;
        if (delta.x > 0) minDeltaX = Math.min(minDeltaX, allowedDelta);
      }
      if (newZ < minZ) {
        const allowedDelta = minZ - position.z;
        if (allowedDelta > minDeltaZ) minDeltaZ = allowedDelta;
        if (delta.z < 0) maxDeltaZ = Math.max(maxDeltaZ, allowedDelta);
      }
      if (newZ > maxZ) {
        const allowedDelta = maxZ - position.z;
        if (allowedDelta < maxDeltaZ) maxDeltaZ = allowedDelta;
        if (delta.z > 0) minDeltaZ = Math.min(minDeltaZ, allowedDelta);
      }
    });

    const clampedDelta = delta.clone();
    clampedDelta.x = THREE.MathUtils.clamp(delta.x, minDeltaX, maxDeltaX);
    clampedDelta.z = THREE.MathUtils.clamp(delta.z, minDeltaZ, maxDeltaZ);
    return clampedDelta;
  }

  /**
   * Clamp a ghost's desired position to stay within room bounds.
   * Honors authored collision-box center offset so pieces with off-center collision
   * volumes (e.g. bonbon3-f, where the closed box is shifted -0.16 along Z) clamp
   * against the actual collision extent rather than the mesh origin.
   * @param {import('../furniture/GhostFurniture.js').GhostFurniture} ghostFurniture
   * @param {THREE.Vector3} position
   * @returns {THREE.Vector3}
   */
  clampGhostPosition(ghostFurniture, position) {
    const roomBounds = this.getRoomInnerBounds();
    if (!roomBounds) return position;

    const mesh = ghostFurniture.getMesh();
    if (!mesh) return position;

    const localSize = ghostFurniture.getLocalSize();
    const halfWidth = localSize.x / 2;
    const halfDepth = localSize.z / 2;
    const centerOffset = ghostFurniture.getCollisionCenterOffset?.() ?? { x: 0, z: 0 };

    const clamped = position.clone();
    // Center of collision volume = position + centerOffset; clamp that into room bounds.
    clamped.x = THREE.MathUtils.clamp(
      position.x,
      -roomBounds.halfWidth + halfWidth - centerOffset.x,
      roomBounds.halfWidth - halfWidth - centerOffset.x
    );
    clamped.z = THREE.MathUtils.clamp(
      position.z,
      -roomBounds.halfDepth + halfDepth - centerOffset.z,
      roomBounds.halfDepth - halfDepth - centerOffset.z
    );
    return clamped;
  }

  /**
   * Check whether a snap position keeps a furniture piece fully inside room bounds.
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement} furniture
   * @param {THREE.Vector3} position
   * @returns {boolean}
   */
  isSnapPositionInBounds(furniture, position) {
    const roomBounds = this.getRoomInnerBounds();
    if (!roomBounds) return true;

    const extents = this.getFurnitureHalfExtents(furniture);
    if (!extents) return true;

    return (
      position.x - extents.halfWidth >= -roomBounds.halfWidth &&
      position.x + extents.halfWidth <= roomBounds.halfWidth &&
      position.z - extents.halfDepth >= -roomBounds.halfDepth &&
      position.z + extents.halfDepth <= roomBounds.halfDepth
    );
  }

  /**
   * Compute world XZ center of a sub-box given a parent center position and rotation.
   * The offset is rotated by the parent's Y rotation into world space.
   * @param {{x:number,y:number,z:number}} offset - Local offset of the sub-box center
   * @param {THREE.Vector3} centerPos - World position of the parent mesh origin
   * @param {number} cos - Math.cos(rotation)
   * @param {number} sin - Math.sin(rotation)
   * @returns {{cx:number, cz:number}}
   */
  _subBoxWorldXZ(offset, centerPos, cos, sin) {
    return {
      cx: centerPos.x + offset.x * cos + offset.z * sin,
      cz: centerPos.z - offset.x * sin + offset.z * cos,
    };
  }

  /**
   * Compute the AABB half-extents of a sub-box at a given rotation.
   * @param {{x:number,y:number,z:number}} size - Sub-box size
   * @param {number} absCos - Math.abs(Math.cos(rotation))
   * @param {number} absSin - Math.abs(Math.sin(rotation))
   * @returns {{hw:number, hd:number}}
   */
  _subBoxHalfExtents(size, absCos, absSin) {
    return {
      hw: (size.x * absCos + size.z * absSin) / 2,
      hd: (size.x * absSin + size.z * absCos) / 2,
    };
  }

  /**
   * Test whether two AABBs overlap in all three axes.
   * Y is included so collision boxes authored at different heights (e.g. a base seat
   * volume and an elevated tray) don't falsely block each other when only their
   * XZ footprints overlap. Pass hh = Infinity to skip the Y axis.
   * @returns {boolean}
   */
  _aabbsOverlap(hw1, hd1, hh1, cx1, cz1, cy1, hw2, hd2, hh2, cx2, cz2, cy2) {
    return (
      Math.abs(cx1 - cx2) < hw1 + hw2 - CollisionSystem.COLLISION_EPSILON &&
      Math.abs(cz1 - cz2) < hd1 + hd2 - CollisionSystem.COLLISION_EPSILON &&
      Math.abs(cy1 - cy2) < hh1 + hh2 - CollisionSystem.COLLISION_EPSILON
    );
  }

  /**
   * Test whether any sub-box of A overlaps any sub-box of B on the XZ plane.
   * Falls back to single-box AABB when collisionBoxes is null.
   *
   * @param {Array|null} boxesA - collisionBoxes array or null
   * @param {THREE.Vector3} posA - World position of A's mesh origin
   * @param {number} rotA - Y rotation of A (radians)
   * @param {{halfWidth,halfDepth}|null} extentsA - Pre-computed single-box extents (fallback)
   * @param {Array|null} boxesB - collisionBoxes array or null
   * @param {THREE.Vector3} posB - World position of B's mesh origin
   * @param {number} rotB - Y rotation of B (radians)
   * @param {{halfWidth,halfDepth}|null} extentsB - Pre-computed single-box extents (fallback)
   * @returns {boolean}
   */
  _multiBoxOverlap(boxesA, posA, rotA, extentsA, boxesB, posB, rotB, extentsB) {
    const subA = this._worldSubBoxes(boxesA, posA, rotA, extentsA);
    const subB = this._worldSubBoxes(boxesB, posB, rotB, extentsB);
    for (const a of subA) {
      for (const b of subB) {
        if (this._aabbsOverlap(a.hw, a.hd, a.hh, a.cx, a.cz, a.cy, b.hw, b.hd, b.hh, b.cx, b.cz, b.cy)) return true;
      }
    }
    return false;
  }

  // World-space AABBs of a piece's authored sub-boxes, or its single union box when it has none.
  _worldSubBoxes(boxes, pos, rot, extents) {
    if (!boxes) {
      return [{
        cx: pos.x,
        cy: pos.y + (extents.localCenterY ?? 0),
        cz: pos.z,
        hw: extents.halfWidth,
        hh: extents.halfHeight ?? Infinity,
        hd: extents.halfDepth,
      }];
    }
    const cos = Math.cos(rot), sin = Math.sin(rot);
    const absCos = Math.abs(cos), absSin = Math.abs(sin);
    return boxes.map(cb => {
      const { cx, cz } = this._subBoxWorldXZ(cb.offset, pos, cos, sin);
      const { hw, hd } = this._subBoxHalfExtents(cb.size, absCos, absSin);
      return { cx, cy: pos.y + (cb.offset?.y ?? 0), cz, hw, hh: cb.size.y / 2, hd };
    });
  }

  /**
   * Check if a furniture piece at a given position collides with any other module.
   * Supports multi-box collision shapes (furniture.collisionBoxes) for L-shaped pieces.
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement} furniture
   * @param {THREE.Vector3} position
   * @param {Set} excludeSet
   * @param {number|null} overrideRotation - When non-null, compute the furniture's AABB
   *   at this Y rotation instead of its current mesh rotation. Pass `snapResult.rotation`
   *   here to validate collision at the snap destination rather than the drag rotation.
   * @returns {boolean}
   */
  checkFurnitureCollisions(furniture, position, excludeSet = new Set(), overrideRotation = null) {
    if (!this.collisionEnabled) return false;
    const extents = this.getFurnitureHalfExtents(furniture, overrideRotation);
    if (!extents) return false;

    // Resolve the rotation used for multi-box offset projection
    let rotA;
    if (overrideRotation !== null) {
      rotA = overrideRotation;
    } else {
      const mesh = furniture.getMesh();
      if (!mesh) return false;
      rotA = getWorldYRotation(mesh);
    }

    const allFurniture = this.app.getFurnitureManager()?.getAllFurniture() || [];

    for (const other of allFurniture) {
      if (other === furniture) continue;
      if (excludeSet.has(other)) continue;
      if (!isFreeStanding(other)) continue;

      const otherExtents = this.getFurnitureHalfExtents(other);
      if (!otherExtents) continue;

      const otherMesh = other.getMesh();
      const otherPos = otherMesh?.position;
      if (!otherPos) continue;

      // Fast union-AABB rejection in XZ — if they can't overlap on the floor plane,
      // skip the precise test entirely. Y is intentionally not part of the fast check
      // (the precise per-sub-box test covers it).
      const fastOverlapX = Math.abs(position.x - otherPos.x) < extents.halfWidth + otherExtents.halfWidth - CollisionSystem.COLLISION_EPSILON;
      const fastOverlapZ = Math.abs(position.z - otherPos.z) < extents.halfDepth + otherExtents.halfDepth - CollisionSystem.COLLISION_EPSILON;
      if (!fastOverlapX || !fastOverlapZ) continue;

      // Precise per-sub-box test (handles both multi-box and single-box pieces, in all three axes)
      const rotB = getWorldYRotation(otherMesh);

      if (this._multiBoxOverlap(
        furniture.collisionBoxes, position, rotA, extents,
        other.collisionBoxes, otherPos, rotB, otherExtents
      )) return true;
    }
    return false;
  }

  // Maximum allowed penetration depth into the parent (metres).
  // Valid face-to-face connections barely touch; inside placements overlap in both axes.
  // Set to 0.15 to accommodate corner pieces (R1/R2) whose L-shaped AABB extends
  // ~0.12 m past their connecting face. Normal rectangular modules have ~0 overlap
  // on the connecting axis, so 0.15 is still conservative for them. True inside
  // placements produce both-axis overlap > 0.25.
  static MAX_PARENT_PENETRATION = 0.15;

  // Penetration allowed when either piece declares `properties.exactCollisionBox`: its box is
  // its true footprint, so a snap that sinks into it is wrong at any depth past rounding. The
  // scaled threshold above lets a 25 cm bard R sink 12 cm into a seat or another R.
  static EXACT_BOX_PENETRATION = 0.01;

  // Maximum allowed true (oriented) penetration depth for curve-edge connections
  // (matacao3 SM/SG). Used by the SAT path below instead of MAX_PARENT_PENETRATION:
  // because the oriented test has no rotation-inflation slop, legitimate angled
  // face-to-face touches penetrate ~0 m, so this can be tight. Gross overlaps (a
  // module dropped on top of a sibling) penetrate well past this and are rejected.
  static CURVE_PENETRATION_MARGIN = 0.2;

  /**
   * Build a list of world-space sub-boxes for a child at the snap pose.
   * Handles FurnitureElement (mesh-local collisionBoxes → rotate by childRotation,
   * or boundingBox fallback) and GhostFurniture (authored boxes → rotate, else
   * getLocalSize/getCollisionCenterOffset which are already world-axis-aligned).
   * @returns {Array<{cx,cy,cz,hw,hh,hd}>|null}
   */
  _buildChildSubBoxesAtSnap(child, position, childRotation) {
    const cosR = Math.cos(childRotation), sinR = Math.sin(childRotation);
    const absCos = Math.abs(cosR), absSin = Math.abs(sinR);

    if (child.collisionBoxes?.length > 0) {
      return child.collisionBoxes.map(cb => {
        const { cx, cz } = this._subBoxWorldXZ(cb.offset, position, cosR, sinR);
        const { hw, hd } = this._subBoxHalfExtents(cb.size, absCos, absSin);
        return { cx, cy: position.y + (cb.offset.y ?? 0), cz, hw, hh: cb.size.y / 2, hd,
                 hx: cb.size.x / 2, hz: cb.size.z / 2, cos: cosR, sin: sinR };
      });
    }
    if (child.boundingBox) {
      const bb = child.boundingBox;
      const off = bb.localCenterOffset ?? { x: 0, y: 0, z: 0 };
      const { cx, cz } = this._subBoxWorldXZ(off, position, cosR, sinR);
      const hw = (bb.size.x * absCos + bb.size.z * absSin) / 2;
      const hd = (bb.size.x * absSin + bb.size.z * absCos) / 2;
      return [{ cx, cy: position.y + (off.y ?? 0), cz, hw, hh: bb.size.y / 2, hd,
                hx: bb.size.x / 2, hz: bb.size.z / 2, cos: cosR, sin: sinR }];
    }
    if (child.getCollisionBoxes) {
      const boxes = child.getCollisionBoxes();
      if (boxes?.length > 0) {
        return boxes.map(cb => {
          const { cx, cz } = this._subBoxWorldXZ(cb.offset, position, cosR, sinR);
          const { hw, hd } = this._subBoxHalfExtents(cb.size, absCos, absSin);
          return { cx, cy: position.y + (cb.offset.y ?? 0), cz, hw, hh: cb.size.y / 2, hd,
                 hx: cb.size.x / 2, hz: cb.size.z / 2, cos: cosR, sin: sinR };
        });
      }
    }
    if (child.getLocalSize) {
      // getLocalSize/getCollisionCenterOffset are world-axis-aligned at current rotation,
      // so we apply the offset directly without further rotation.
      const sz = child.getLocalSize();
      const off = child.getCollisionCenterOffset?.() ?? { x: 0, y: 0, z: 0 };
      return [{
        cx: position.x + (off.x ?? 0),
        cy: position.y + (off.y ?? 0),
        cz: position.z + (off.z ?? 0),
        hw: sz.x / 2,
        hh: (sz.y ?? Infinity) / 2,
        hd: sz.z / 2,
        // getLocalSize is already world-axis-aligned at the current rotation.
        hx: sz.x / 2, hz: sz.z / 2, cos: 1, sin: 0,
      }];
    }
    return null;
  }

  /**
   * Build a list of world-space sub-boxes for a placed parent at its current pose.
   * @returns {Array<{cx,cy,cz,hw,hh,hd}>|null}
   */
  _buildParentSubBoxes(parent) {
    const mesh = parent.getMesh();
    if (!mesh) return null;
    const pos = mesh.position;
    const rot = getWorldYRotation(mesh);
    const cosR = Math.cos(rot), sinR = Math.sin(rot);
    const absCos = Math.abs(cosR), absSin = Math.abs(sinR);

    if (parent.collisionBoxes?.length > 0) {
      return parent.collisionBoxes.map(cb => {
        const { cx, cz } = this._subBoxWorldXZ(cb.offset, pos, cosR, sinR);
        const { hw, hd } = this._subBoxHalfExtents(cb.size, absCos, absSin);
        return { cx, cy: pos.y + (cb.offset.y ?? 0), cz, hw, hh: cb.size.y / 2, hd,
                 hx: cb.size.x / 2, hz: cb.size.z / 2, cos: cosR, sin: sinR };
      });
    }
    if (parent.boundingBox) {
      const bb = parent.boundingBox;
      const off = bb.localCenterOffset ?? { x: 0, y: 0, z: 0 };
      const { cx, cz } = this._subBoxWorldXZ(off, pos, cosR, sinR);
      const hw = (bb.size.x * absCos + bb.size.z * absSin) / 2;
      const hd = (bb.size.x * absSin + bb.size.z * absCos) / 2;
      return [{ cx, cy: pos.y + (off.y ?? 0), cz, hw, hh: bb.size.y / 2, hd,
                hx: bb.size.x / 2, hz: bb.size.z / 2, cos: cosR, sin: sinR }];
    }
    return null;
  }

  /**
   * Check if placing a child at `position` would penetrate deeply into the parent.
   *
   * Walks every (child sub-box, parent sub-box) pair. A pair counts as an inside
   * placement when XZ overlap on BOTH axes exceeds the per-axis threshold AND the
   * Y ranges overlap non-trivially. The Y check lets a tray sub-box at top-layer Y
   * legitimately sit over a base-layer seat without being flagged as penetration,
   * while still rejecting the same tray when it crashes into a backrest authored
   * at the same top-layer Y.
   *
   * The per-axis threshold scales with the smaller of the two sub-boxes:
   *   threshold = min(MAX_PARENT_PENETRATION, c.hExtent, p.hExtent)
   * This keeps the original behaviour for normal-sized boxes (still 0.15 m, so
   * L-corner pieces extending ~0.12 m past their connecting face aren't falsely
   * flagged) while making thin protrusions (e.g. a 0.10 m deep flange on the front
   * of a piece) tight enough to actually detect their intrusion — a constant 0.15
   * threshold could never fire for a sub-box only 0.10 m thick on the axis.
   * When either piece declares `exactCollisionBox`, both thresholds are
   * EXACT_BOX_PENETRATION instead.
   *
   * Works with both FurnitureElement (drag-snap) and GhostFurniture (place-snap).
   *
   * @param {Object} child - FurnitureElement or GhostFurniture
   * When `oriented` is true (curve-edge connections — matacao3 SM/SG, whose authored
   * collision boxes are thin slices following the curve), the overlap is measured with
   * an oriented SAT test instead of the axis-aligned one. AABB-enclosing a slice
   * rotated to a fan angle inflates it (a 0.11×0.94 slice at 45° becomes ~0.74×0.74),
   * which falsely reports 3–29 cm of penetration on a clean touch; the SAT test uses
   * the true rotated extents and so only fires on real overlap.
   *
   * @param {Object} child - FurnitureElement or GhostFurniture
   * @param {THREE.Vector3} position - proposed child center position
   * @param {number} childRotation - proposed child Y rotation (radians)
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement} parent
   * @param {boolean} [oriented] - use the oriented (SAT) overlap test for curve edges
   * @returns {boolean} true if the snap would place child inside parent
   */
  checkSnapPenetration(child, position, childRotation, parent, oriented = false) {
    if (!this.collisionEnabled) return false;
    if (!parent.getMesh()) return false;
    const childSubs  = this._buildChildSubBoxesAtSnap(child, position, childRotation);
    if (!childSubs) return false;
    const parentSubs = this._buildParentSubBoxes(parent);
    if (!parentSubs) return false;

    if (oriented) return this._orientedSubBoxesPenetrate(childSubs, parentSubs);

    const MAX = CollisionSystem.MAX_PARENT_PENETRATION;
    const EPS = CollisionSystem.COLLISION_EPSILON;
    const exact = child.furnitureData?.properties?.exactCollisionBox || parent.furnitureData?.properties?.exactCollisionBox;

    for (const c of childSubs) {
      for (const p of parentSubs) {
        const thresholdX = exact ? CollisionSystem.EXACT_BOX_PENETRATION : Math.min(MAX, c.hw, p.hw);
        const ovX = c.hw + p.hw - Math.abs(c.cx - p.cx);
        if (ovX <= thresholdX) continue;
        const thresholdZ = exact ? CollisionSystem.EXACT_BOX_PENETRATION : Math.min(MAX, c.hd, p.hd);
        const ovZ = c.hd + p.hd - Math.abs(c.cz - p.cz);
        if (ovZ <= thresholdZ) continue;
        const ovY = c.hh + p.hh - Math.abs(c.cy - p.cy);
        if (ovY <= EPS) continue;
        return true; // inside placement detected
      }
    }
    return false;
  }

  /**
   * Oriented (SAT) version of the penetration test for curve-edge connections.
   * Walks every (child sub-box, parent sub-box) pair and flags the snap when any
   * pair overlaps — in the XZ plane by more than CURVE_PENETRATION_MARGIN of true
   * (un-inflated) penetration depth, AND with overlapping Y ranges. Each sub-box
   * carries its own rotation (cos/sin) and local half-extents (hx/hz).
   */
  _orientedSubBoxesPenetrate(childSubs, parentSubs) {
    const margin = CollisionSystem.CURVE_PENETRATION_MARGIN;
    const EPS = CollisionSystem.COLLISION_EPSILON;
    for (const c of childSubs) {
      for (const p of parentSubs) {
        const ovY = c.hh + p.hh - Math.abs(c.cy - p.cy);
        if (ovY <= EPS) continue;
        if (orientedXZPenetration(c, p) > margin) return true;
      }
    }
    return false;
  }

  /**
   * Check if a ghost furniture piece at a given position collides with any existing module.
   * Uses multi-box collision shapes for `other` pieces when available.
   * @param {import('../furniture/GhostFurniture.js').GhostFurniture} ghostFurniture
   * @param {THREE.Vector3} position
   * @param {Set} excludeSet
   * @returns {boolean}
   */
  checkGhostFurnitureCollisions(ghostFurniture, position, excludeSet = new Set()) {
    if (!this.collisionEnabled) return false;
    const mesh = ghostFurniture.getMesh();
    if (!mesh) return false;

    // getLocalSize() returns the world-aligned size at the ghost's current rotation
    // (sourced from authored collisionBox(es) when present, otherwise raw mesh AABB).
    const localSize = ghostFurniture.getLocalSize();
    const halfWidth = localSize.x / 2;
    const halfDepth = localSize.z / 2;
    const halfHeight = (localSize.y ?? Infinity) / 2;
    const centerOffset = ghostFurniture.getCollisionCenterOffset?.() ?? { x: 0, y: 0, z: 0 };
    // Effective collision center in world space (XZ only for fast rejection)
    const cx = position.x + centerOffset.x;
    const cz = position.z + centerOffset.z;

    // Authored multi-box for the ghost, if any. Multi-box offsets are mesh-local
    // (pre-rotation), and _multiBoxOverlap will rotate them by rotA.
    const ghostBoxes = ghostFurniture.getCollisionBoxes?.() ?? null;
    const ghostRot = ghostFurniture.getCurrentRotation?.() ?? 0;

    const allFurniture = this.app.getFurnitureManager()?.getAllFurniture() || [];

    for (const other of allFurniture) {
      if (excludeSet.has(other)) continue;
      if (!isFreeStanding(other)) continue;

      const otherExtents = this.getFurnitureHalfExtents(other);
      if (!otherExtents) continue;

      const otherMesh = other.getMesh();
      const otherPos = otherMesh?.position;
      if (!otherPos) continue;

      // Fast union-AABB rejection in XZ (uses offset-corrected center). Y is
      // intentionally not part of the fast check — the precise per-sub-box test
      // (which knows per-box Y) handles vertical separation.
      const fastOverlapX = Math.abs(cx - otherPos.x) < halfWidth + otherExtents.halfWidth - CollisionSystem.COLLISION_EPSILON;
      const fastOverlapZ = Math.abs(cz - otherPos.z) < halfDepth + otherExtents.halfDepth - CollisionSystem.COLLISION_EPSILON;
      if (!fastOverlapX || !fastOverlapZ) continue;

      const rotB = getWorldYRotation(otherMesh);
      // Ghost mesh.position is the parent of the multi-box offsets; pass `position`
      // (not the offset-corrected center) so the rotation math composes correctly.
      // For the single-box fallback we hand the fallback path Y via halfHeight /
      // localCenterY so the precise test still rejects vertically-separated pieces.
      const ghostExtents = {
        halfWidth,
        halfDepth,
        halfHeight,
        localCenterY: centerOffset.y ?? 0,
      };
      if (this._multiBoxOverlap(
        ghostBoxes, position, ghostRot, ghostExtents,
        other.collisionBoxes, otherPos, rotB, otherExtents
      )) return true;
    }
    return false;
  }

  /**
   * Check whether opening a storage compartment would intrude on another module.
   * Tests `clearanceBox` (in mesh-local frame) against every other module's
   * collisionBoxes/boundingBox. The clearance box represents only the additional
   * swept volume the lid/armrest occupies when transitioning closed→open, so the
   * test naturally ignores the furniture's own closed-state footprint.
   *
   * Also blocks if the clearance volume would protrude past room bounds.
   *
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement} furniture
   * @param {{size:{x,y,z}, offset?:{x,y,z}}} clearanceBox - mesh-local frame
   * @returns {boolean} true if opening is blocked
   */
  checkOpenClearance(furniture, clearanceBox) {
    if (!this.collisionEnabled) return false;
    if (!clearanceBox?.size) return false;
    const mesh = furniture.getMesh();
    if (!mesh) return false;

    const rotA = getWorldYRotation(mesh);
    const cosA = Math.cos(rotA), sinA = Math.sin(rotA);
    const absCosA = Math.abs(cosA), absSinA = Math.abs(sinA);

    const offset = clearanceBox.offset ?? { x: 0, y: 0, z: 0 };
    const { cx, cz } = this._subBoxWorldXZ(offset, mesh.position, cosA, sinA);
    const { hw, hd } = this._subBoxHalfExtents(clearanceBox.size, absCosA, absSinA);
    const cy = mesh.position.y + (offset.y ?? 0);
    const hh = clearanceBox.size.y / 2;

    // Room bounds
    const roomBounds = this.getRoomInnerBounds();
    if (roomBounds) {
      if (cx - hw < -roomBounds.halfWidth || cx + hw > roomBounds.halfWidth) return true;
      if (cz - hd < -roomBounds.halfDepth || cz + hd > roomBounds.halfDepth) return true;
    }

    // Other modules
    const allFurniture = this.app.getFurnitureManager()?.getAllFurniture() || [];
    for (const other of allFurniture) {
      if (other === furniture) continue;
      if (!isFreeStanding(other)) continue;

      const otherExtents = this.getFurnitureHalfExtents(other);
      if (!otherExtents) continue;
      const otherMesh = other.getMesh();
      const otherPos = otherMesh?.position;
      if (!otherPos) continue;

      // Fast union-AABB rejection in XZ
      const fastOverlapX = Math.abs(cx - otherPos.x) < hw + otherExtents.halfWidth - CollisionSystem.COLLISION_EPSILON;
      const fastOverlapZ = Math.abs(cz - otherPos.z) < hd + otherExtents.halfDepth - CollisionSystem.COLLISION_EPSILON;
      if (!fastOverlapX || !fastOverlapZ) continue;

      const rotB = getWorldYRotation(otherMesh);
      const cosB = Math.cos(rotB), sinB = Math.sin(rotB);
      const absCosB = Math.abs(cosB), absSinB = Math.abs(sinB);

      if (other.collisionBoxes?.length > 0) {
        for (const cb of other.collisionBoxes) {
          const { cx: bcx, cz: bcz } = this._subBoxWorldXZ(cb.offset, otherPos, cosB, sinB);
          const { hw: bhw, hd: bhd } = this._subBoxHalfExtents(cb.size, absCosB, absSinB);
          const bcy = otherPos.y + (cb.offset?.y ?? 0);
          const bhh = cb.size.y / 2;
          if (this._aabbsOverlap(hw, hd, hh, cx, cz, cy, bhw, bhd, bhh, bcx, bcz, bcy)) return true;
        }
      } else {
        // Single-box fallback via union boundingBox
        const bb = other.boundingBox;
        const off = bb?.localCenterOffset ?? { x: 0, y: 0, z: 0 };
        const { cx: bcx, cz: bcz } = this._subBoxWorldXZ(off, otherPos, cosB, sinB);
        const bhw = otherExtents.halfWidth;
        const bhd = otherExtents.halfDepth;
        const bhh = otherExtents.halfHeight ?? Infinity;
        const bcy = otherPos.y + (otherExtents.localCenterY ?? 0);
        if (this._aabbsOverlap(hw, hd, hh, cx, cz, cy, bhw, bhd, bhh, bcx, bcz, bcy)) return true;
      }
    }

    return false;
  }

  // Clearance radius (m) kept between the camera and furniture, sized against the
  // 0.1 m near plane so geometry never pokes through the lens.
  static CAMERA_CLEARANCE = 0.22;

  /**
   * Push `point` (the camera) out of any furniture collision volume, ejecting along
   * the axis of least penetration. Y is included, so an overhead orbit is untouched.
   * Mutates and returns `point`; no-op when there is no furniture.
   */
  resolveCameraClearance(point, padding = CollisionSystem.CAMERA_CLEARANCE) {
    const allFurniture = this.app.getFurnitureManager()?.getAllFurniture();
    if (!allFurniture?.length) return point;

    const eject = (cx, cy, cz, hw, hh, hd) => {
      const dx = point.x - cx; const ox = hw - Math.abs(dx); if (ox <= 0) return;
      const dz = point.z - cz; const oz = hd - Math.abs(dz); if (oz <= 0) return;
      const dy = point.y - cy; const oy = hh - Math.abs(dy); if (oy <= 0) return;
      if (ox <= oz && ox <= oy)      point.x += dx >= 0 ? ox : -ox;
      else if (oz <= oy)             point.z += dz >= 0 ? oz : -oz;
      else                           point.y += dy >= 0 ? oy : -oy;
      this._camClrMoved = true;
    };

    // Two passes so a point wedged between two overlapping boxes settles cleanly.
    for (let pass = 0; pass < 2; pass++) {
      this._camClrMoved = false;
      for (const furniture of allFurniture) {
        forEachWorldCollisionBox(furniture, padding, eject);
      }
      if (!this._camClrMoved) break;
    }
    return point;
  }

  /**
   * Resolve a module's desired position to avoid colliding with other modules.
   * Blocks movement entirely on collision (returns current position on the XZ plane).
   * @param {import('../furniture/FurnitureElement.js').FurnitureElement} furniture
   * @param {THREE.Vector3} desiredPosition
   * @returns {THREE.Vector3}
   */
  resolveModuleCollision(furniture, desiredPosition) {
    if (!isFreeStanding(furniture)) return desiredPosition;

    const excludeSet = furniture.getAllGroupModules();

    if (!this.checkFurnitureCollisions(furniture, desiredPosition, excludeSet)) {
      return desiredPosition;
    }

    const currentPosition = furniture.getMesh()?.position;
    if (!currentPosition) return desiredPosition;

    return new THREE.Vector3(currentPosition.x, desiredPosition.y, currentPosition.z);
  }
}